import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

const paginationVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-background hover:bg-accent hover:text-accent-foreground',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 w-8 p-0',
        lg: 'h-10 px-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface PaginationProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof paginationVariants> {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  siblingCount?: number;
}

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      className,
      variant,
      size,
      currentPage,
      totalPages,
      onPageChange,
      showFirstLast = true,
      siblingCount = 1,
      ...props
    },
    ref
  ) => {
    const generatePaginationItems = () => {
      const items: (number | 'ellipsis')[] = [];
      const maxVisibleItems = siblingCount * 2 + 3; // siblings + current + first/last

      if (totalPages <= maxVisibleItems) {
        // Show all pages if total pages is less than max visible items
        for (let i = 1; i <= totalPages; i++) {
          items.push(i);
        }
      } else {
        // Always show first page
        items.push(1);

        // Calculate start and end of sibling pages
        const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

        // Add ellipsis if needed before left siblings
        if (leftSiblingIndex > 2) {
          items.push('ellipsis');
        }

        // Add sibling pages
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          items.push(i);
        }

        // Add ellipsis if needed after right siblings
        if (rightSiblingIndex < totalPages - 1) {
          items.push('ellipsis');
        }

        // Always show last page
        items.push(totalPages);
      }

      return items;
    };

    const items = generatePaginationItems();

    return (
      <div
        ref={ref}
        className={cn('flex items-center space-x-1', className)}
        {...props}
      >
        {showFirstLast && (
          <button
            className={cn(paginationVariants({ variant, size }))}
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            aria-label="Go to first page"
          >
            <ChevronLeft className="h-4 w-4" />
            <ChevronLeft className="h-4 w-4 -ml-2" />
          </button>
        )}
        <button
          className={cn(paginationVariants({ variant, size }))}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {items.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="flex h-9 w-9 items-center justify-center"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            );
          }

          return (
            <button
              key={item}
              className={cn(
                paginationVariants({ variant, size }),
                item === currentPage &&
                  'bg-primary text-primary-foreground hover:bg-primary/90'
              )}
              onClick={() => onPageChange(item)}
              aria-label={`Go to page ${item}`}
              aria-current={item === currentPage ? 'page' : undefined}
            >
              {item}
            </button>
          );
        })}

        <button
          className={cn(paginationVariants({ variant, size }))}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        {showFirstLast && (
          <button
            className={cn(paginationVariants({ variant, size }))}
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            aria-label="Go to last page"
          >
            <ChevronRight className="h-4 w-4" />
            <ChevronRight className="h-4 w-4 -ml-2" />
          </button>
        )}
      </div>
    );
  }
);
Pagination.displayName = 'Pagination';

export { Pagination, paginationVariants }; 